import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import { Form as BootstrapForm, Button } from "react-bootstrap";
import "./style.css"; // Custom styles for the modal
import ErrorMessageComponent from "../../modules/common/ErrorMessageComponent";
import SubmitButton from "../../modules/common/SubmitButton";
import { useDispatch, useSelector } from "react-redux";
import {
  CreateProrperty,
  GetProperties,
  UpdateProperty,
} from "../../redux/features/properties/propertyActions";
import { useNavigate } from "react-router-dom";
import { objectToFormData } from "../../utlis/common";
import { setSelectedProperty } from "../../redux/features/properties/propertySlice";

export const propertyValidationSchema = yup.object().shape({
  name: yup.string().trim().required("Property name is required"),
  address: yup.string().trim().required("Address is required"),
  propertyType: yup
    .string()
    .oneOf(["house", "villa", "apartment"], "Invalid property type")
    .required("Property type is required"),
  description: yup.string().trim().required("Description is required"),
  propertySize: yup
    .number()
    .positive("Property size must be a positive number")
    .required("Property size is required"),
  bedrooms: yup
    .number()
    .integer("Bedrooms must be a whole number")
    .min(1, "There must be at least 1 bedroom")
    .required("Bedrooms count is required"),
  bathrooms: yup
    .number()
    .integer("Bathrooms must be a whole number")
    .min(1, "There must be at least 1 bathroom")
    .required("Bathrooms count is required"),
  coordinate: yup.object().shape({
    latitude: yup.number(),
    longitude: yup.number()
  }),
  facilities: yup.object().shape({
    carParking: yup.boolean(),
    swimming: yup.boolean(),
    gymAndFit: yup.boolean(),
    restaurant: yup.boolean(),
    wifi: yup.boolean(),
    petCenter: yup.boolean(),
    sportsClub: yup.boolean(),
    laundry: yup.boolean(),
  }),
  price: yup
    .number()
    .positive("Price must be a positive number")
    .required("Price is required"),
  images: yup.mixed().required("Images are required"),
});

const PropertyModal = (props) => {
  const filePath = process.env.REACT_APP_IMAGE_URL + 'uploads/'
  const loading = useSelector((state: any) => state.propertiesSlice.loading);
  const property: any = useSelector(
    (state: any) => state.propertiesSlice.selectedProperty
  );
  const [selectedImages, setSelectedImages] = useState<any>([]);
  const dispatch: any = useDispatch();
  const navigate: any = useNavigate();
  const initialValues = {
    name: property?.name || "",
    address: property?.address || "",
    propertyType: property?.propertyType || "",
    description: property?.description || "",
    propertySize: property?.propertySize || "",
    bedrooms: property?.bedrooms || "",
    bathrooms: property?.bathrooms || "",
    coordinate: {
      latitude: property?.coordinate?.latitude || "",
      longitude: property?.coordinate?.longitude || ""
    },
    facilities: {
      carParking: property?.facilities?.carParking || false,
      swimming: property?.facilities?.swimming || false,
      gymAndFit: property?.facilities?.gymAndFit || false,
      restaurant: property?.facilities?.restaurant || false,
      wifi: property?.facilities?.wifi || false,
      petCenter: property?.facilities?.petCenter || false,
      sportsClub: property?.facilities?.sportsClub || false,
      laundry: property?.facilities?.laundry || false,
    },
    price: property?.price || "",
    images: property?.images || [], // Assuming images are stored as an array
  };

  const handleImageChange = (event, setFieldValue) => {
    const files = Array.from(event.target.files);
    setSelectedImages(files);
    setFieldValue("images", files);
  };

  const handleSubmit = (values, { resetForm }) => {
    const formdata = objectToFormData(values);
    if (property) {
      dispatch(
        UpdateProperty({
          id: property?._id,
          values: formdata,
          resetForm,
          navigate,
          loading,
        })
      );
      dispatch(setSelectedProperty(null));
    } else {
      dispatch(CreateProrperty({ values: formdata, resetForm, loading }));
    }
    resetForm();
    setSelectedImages([]); // Reset selected images after submission
    props.handleClose();
  };

  const removeExistingImage = (index, setFieldValue) => {
    // Update form state
  };
  return (
    <Modal show={props.show} onHide={props.handleClose} centered size="lg">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          {property ? "Edit Property" : "Create Property"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-body">
        <Formik
          initialValues={initialValues}
          validationSchema={propertyValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, handleSubmit, setFieldValue }) => (
            <Form noValidate onSubmit={handleSubmit}>
              {/* Property Name */}
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Property Name</BootstrapForm.Label>
                <Field
                  name="name"
                  type="text"
                  className="form-control"
                  placeholder="Enter property name"
                />
                <ErrorMessageComponent name="name" />
              </BootstrapForm.Group>

              {/* Address */}
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Address</BootstrapForm.Label>
                <Field
                  name="address"
                  type="text"
                  className="form-control"
                  placeholder="Enter address"
                />
                <ErrorMessageComponent name="address" />
              </BootstrapForm.Group>

              {/* Property Type */}
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Property Type</BootstrapForm.Label>
                <Field as="select" name="propertyType" className="form-control">
                  <option value="">Select Property Type</option>
                  <option value="house">House</option>
                  <option value="villa">Villa</option>
                  <option value="apartment">Apartment</option>
                </Field>
                <ErrorMessageComponent name="propertyType" />
              </BootstrapForm.Group>

              {/* Description */}
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Description</BootstrapForm.Label>
                <Field
                  as="textarea"
                  name="description"
                  className="form-control"
                  placeholder="Enter description"
                />
                <ErrorMessageComponent name="description" />
              </BootstrapForm.Group>

              {/* Bedrooms & Bathrooms */}
              <div className="d-flex gap-3">
                <BootstrapForm.Group className="mb-3 w-50">
                  <BootstrapForm.Label>Bedrooms</BootstrapForm.Label>
                  <Field
                    name="bedrooms"
                    type="number"
                    className="form-control"
                  />
                  <ErrorMessageComponent name="bedrooms" />
                </BootstrapForm.Group>

                <BootstrapForm.Group className="mb-3 w-50">
                  <BootstrapForm.Label>Bathrooms</BootstrapForm.Label>
                  <Field
                    name="bathrooms"
                    type="number"
                    className="form-control"
                  />
                  <ErrorMessageComponent name="bathrooms" />
                </BootstrapForm.Group>
              </div>
              {/* Coordinate */}
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Coordinate</BootstrapForm.Label>
                <div className="d-flex flex-wrap gap-3">

                  <label
                    className="d-flex align-items-center gap-1"
                  >
                    <Field name="coordinate.latitude"
                      type="number"
                      className="form-control"
                      placeholder="Enter latitude" />
                  </label>
                  <label
                    className="d-flex align-items-center gap-1"
                  >
                    <Field name="coordinate.longitude"
                      type="number"
                      className="form-control"
                      placeholder="Enter Longitude" />
                  </label>
                </div>
              </BootstrapForm.Group>
              {/* Facilities */}
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Facilities</BootstrapForm.Label>
                <div className="d-flex flex-wrap gap-3">
                  {[
                    "carParking",
                    "swimming",
                    "gymAndFit",
                    "restaurant",
                    "wifi",
                    "petCenter",
                    "sportsClub",
                    "laundry",
                  ].map((facility) => (
                    <label
                      key={facility}
                      className="d-flex align-items-center gap-1"
                    >
                      <Field type="checkbox" name={`facilities.${facility}`} />
                      {facility.replace(/([A-Z])/g, " $1").trim()}
                    </label>
                  ))}
                </div>
              </BootstrapForm.Group>
              {/* Price */}
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Property Size</BootstrapForm.Label>
                <Field
                  name="propertySize"
                  type="number"
                  className="form-control"
                  placeholder="Enter property size"
                />
                <ErrorMessageComponent name="propertySize" />
              </BootstrapForm.Group>
              {/* Price */}
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Price ($)</BootstrapForm.Label>
                <Field
                  name="price"
                  type="number"
                  className="form-control"
                  placeholder="Enter price"
                />
                <ErrorMessageComponent name="price" />
              </BootstrapForm.Group>

              {/* Image Upload */}
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Upload Images</BootstrapForm.Label>
                <input
                  type="file"
                  className="form-control"
                  multiple
                  accept="image/*"
                  onChange={(event) => handleImageChange(event, setFieldValue)}
                />
                <ErrorMessage
                  name="images"
                  component="div"
                  className="text-danger"
                />

                {/* Show Preview of Selected Images */}
                <div className="d-flex flex-wrap mt-2">
                  {selectedImages.map((image, index) => (
                    <div key={index} className="preview-img-container">
                      <img
                        height={100}
                        width={100}
                        src={URL.createObjectURL(image)}
                        alt="preview"
                        className="preview-img"
                      />
                    </div>
                  ))}
                </div>
              </BootstrapForm.Group>
              <div className="d-flex flex-wrap mt-3">
                {/* Show existing images */}
                {property?.images?.map((imgUrl, index) => (
                  <div key={index} className="position-relative me-2">
                    <img
                      src={filePath + imgUrl}
                      alt="Existing"
                      className="img-thumbnail"
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                ))}
              </div>
              {/* Buttons */}
              <div className="d-flex justify-content-end">
                <Button
                  variant="secondary"
                  onClick={props.handleClose}
                  className="me-2"
                >
                  Close
                </Button>
                <SubmitButton title="Create" type="submit" loading={loading} />
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default PropertyModal;
